/**
 * CustomerAutoImportDialog
 *
 * Wizard de cadastro AUTOMÁTICO de cliente a partir de um sistema externo (ERP).
 * Fluxo:
 *   1. Consulta por CPF/CNPJ (via Integration Gateway — multi-provider)
 *   2. Verificação de duplicidade no tenant (cliente e veículos já cadastrados)
 *   3. Conferência dos dados + seleção do que será criado/atualizado
 *   4. Gravação atômica que orquestra as RPCs oficiais no banco.
 *
 * O componente NÃO conhece o provider: recebe a conexão elegível pela
 * capacidade `customer.lookup_document`.
 *
 * RELEASE: 2026-08-23 v1.5.0 - Resolução manual de veículos sem correspondência FIPE:
 *          painel inline com Marca/Modelo/Ano (mesmos seletores do cadastro manual) ou
 *          categoria própria do tenant para tipos fora da FIPE. Impede gravar veículo sem
 *          vínculo relacional válido.
 * RELEASE: 2026-08-23 v1.4.0 - Layout com altura fixa por viewport (padrão do modal de
 *          anotações do chat): cabeçalho e rodapé fixos, corpo com rolagem interna própria
 *          para suportar muitos veículos sem "esticar" o modal.
 * RELEASE: 2026-08-23 v1.3.0 - Sinaliza cliente/veículo já cadastrados, permite

 *          importar somente os veículos novos e atualizar um veículo existente
 *          escolhendo campo a campo o que pode ser sobrescrito. Veículos já
 *          cadastrados não disparam consulta FIPE/placa automática (economia de créditos).
 * RELEASE: 2026-08-23 v1.2.0 - Gravação atômica, espera obrigatória do enriquecimento,
 *          payload canônico e invalidação completa dos caches relacionados.
 * RELEASE: 2026-08-23 v1.1.0 - Enriquecimento FIPE dos veículos importados.
 * RELEASE: 2026-08-23 v1.0.0 - Versão inicial (integração SGCNET).
 */

import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Car,
  CheckCircle2,
  Loader2,
  Mail,
  MapPin,
  Phone,
  RefreshCw,
  Search,
  User,
  Wrench,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { InlineNotice } from '@/components/ui/inline-notice';

import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';
import { applyCPFMask, applyCNPJMask } from '@/lib/input-masks';
import { cn } from '@/lib/utils';
import {
  buildCanonicalImportedVehicle,
  buildVehicleFieldDiffs,
  type UpdatableVehicleField,
  type VehicleFieldDiff,
} from '@/lib/vehicle-import-payload';
import {
  useCustomerDocumentLookup,
  useDocumentLookupConnections,
  type LookupResult,
  type LookupVehicle,
} from '@/hooks/integrations/useCustomerDocumentLookup';
import {
  useImportExistingMatches,
  normalizeIdentifier,
  type ExistingVehicleMatch,
  type ImportExistingMatches,
} from '@/hooks/integrations/useImportExistingMatches';
import {
  useVehicleFipeEnrichment,
  type VehicleEnrichment,
} from '@/hooks/vehicles/useVehicleFipeEnrichment';
import {
  useFipeBrands,
  useFipeModels,
  useFipeYears,
  type FipeVehicleType,
} from '@/hooks/useFipeLookup';
import { useVehiclesModule } from '@/hooks/useVehiclesModule';
import { VEHICLE_TYPE_LABELS, type VehicleType } from '@/hooks/useCustomerVehicles';

// =============================================================================
// TIPOS
// =============================================================================

interface CustomerAutoImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Disparado após a gravação bem-sucedida (id do cliente criado/reaproveitado) */
  onSuccess?: (customerId: string) => void;
}

// =============================================================================
// HELPERS
// =============================================================================

/** Aplica máscara dinâmica: CPF até 11 dígitos, CNPJ acima disso */
function applyDocumentMask(value: string): string {
  const digits = value.replace(/\D+/g, '').slice(0, 14);
  return digits.length > 11 ? applyCNPJMask(digits) : applyCPFMask(digits);
}

/** Monta o rótulo amigável do veículo */
function vehicleLabel(vehicle: LookupVehicle): string {
  return (
    [vehicle.brand_name, vehicle.model_name].filter(Boolean).join(' ') ||
    vehicle.plate ||
    'Veículo sem identificação'
  );
}

/** Rótulo/estilo do status de enriquecimento FIPE exibido em cada veículo */
const ENRICHMENT_META: Record<
  Exclude<VehicleEnrichment['status'], 'idle'>,
  { label: string; className: string }
> = {
  loading: { label: 'Consultando FIPE…', className: 'text-muted-foreground' },
  confirmed: { label: 'FIPE confirmada', className: 'text-emerald-600 dark:text-emerald-400' },
  suggested: { label: 'FIPE sugerida', className: 'text-amber-600 dark:text-amber-400' },
  unmatched: { label: 'Sem correspondência FIPE', className: 'text-muted-foreground' },
  manual: { label: 'Definido manualmente', className: 'text-blue-600 dark:text-blue-400' },
};

/** Campos que exigem FIPE resolvida para poderem ser atualizados */
const FIPE_DEPENDENT_FIELDS: UpdatableVehicleField[] = ['brand', 'model', 'year'];

// =============================================================================
// COMPONENTE
// =============================================================================

export function CustomerAutoImportDialog({
  open,
  onOpenChange,
  onSuccess,
}: CustomerAutoImportDialogProps) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const { connections, defaultConnection, isLoading: isLoadingConnections } =
    useDocumentLookupConnections(open);
  const lookup = useCustomerDocumentLookup();
  const existingMatches = useImportExistingMatches();
  // Enriquecimento FIPE (marca/modelo/ano oficiais) dos veículos
  const { enrichment, enrichVehicle, enrichAll, setManualMatch, reset: resetEnrichment } =
    useVehicleFipeEnrichment();

  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [document, setDocument] = useState('');
  const [result, setResult] = useState<LookupResult | null>(null);
  const [matches, setMatches] = useState<ImportExistingMatches | null>(null);
  const [selectedVehicles, setSelectedVehicles] = useState<Record<string, boolean>>({});
  /** Veículos existentes marcados para atualização */
  const [updateEnabled, setUpdateEnabled] = useState<Record<string, boolean>>({});
  /** Campos autorizados a sobrescrever, por veículo */
  const [fieldSelection, setFieldSelection] = useState<
    Record<string, Partial<Record<UpdatableVehicleField, boolean>>>
  >({});
  const [isSaving, setIsSaving] = useState(false);
  const [isEnriching, setIsEnriching] = useState(false);

  /** Conexão efetiva: a escolhida pelo usuário ou a padrão do tenant */
  const effectiveConnectionId = connectionId ?? defaultConnection?.id ?? null;

  const digits = useMemo(() => document.replace(/\D+/g, ''), [document]);
  const isDocumentValid = digits.length === 11 || digits.length === 14;
  const customerType: 'pf' | 'pj' = digits.length === 14 ? 'pj' : 'pf';

  /** Índice placa/chassi -> veículo já cadastrado no tenant */
  const matchIndex = useMemo(() => {
    const index = new Map<string, ExistingVehicleMatch>();
    (matches?.vehicles ?? []).forEach((item) => {
      const plate = normalizeIdentifier(item.plate);
      const chassis = normalizeIdentifier(item.chassis);
      if (plate) index.set(`P:${plate}`, item);
      if (chassis) index.set(`C:${chassis}`, item);
    });
    return index;
  }, [matches]);

  /** Localiza o cadastro existente correspondente ao veículo do provider */
  const findExisting = useCallback(
    (vehicle: LookupVehicle): ExistingVehicleMatch | null => {
      const plate = normalizeIdentifier(vehicle.plate);
      const chassis = normalizeIdentifier(vehicle.chassis);
      return (
        (plate ? matchIndex.get(`P:${plate}`) : undefined) ??
        (chassis ? matchIndex.get(`C:${chassis}`) : undefined) ??
        null
      );
    },
    [matchIndex],
  );

  /** Reinicia o wizard ao fechar */
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setDocument('');
      setResult(null);
      setMatches(null);
      setSelectedVehicles({});
      setUpdateEnabled({});
      setFieldSelection({});
      resetEnrichment();
      lookup.reset();
      existingMatches.reset();
    }
    onOpenChange(next);
  };

  /** PASSO 1 — consulta no sistema externo + verificação de duplicidade */
  const handleSearch = async () => {
    if (!effectiveConnectionId) {
      toast.error('Nenhuma conexão disponível para consulta automática');
      return;
    }
    setResult(null);
    setMatches(null);
    setUpdateEnabled({});
    setFieldSelection({});
    resetEnrichment();

    try {
      const data = await lookup.mutateAsync({
        connectionId: effectiveConnectionId,
        document: digits,
      });
      setResult(data);

      if (!data.found) {
        toast.info(data.message || 'Nenhum resultado encontrado');
        setSelectedVehicles({});
        return;
      }

      // Duplicidade: cliente e veículos já existentes neste tenant
      const found = await existingMatches.mutateAsync({
        document: digits,
        plates: (data.vehicles ?? []).map((v) => v.plate),
        chassis: (data.vehicles ?? []).map((v) => v.chassis),
        tenantId: currentTenant?.id ?? null,
      });
      setMatches(found);

      const existingKeys = new Set<string>();
      (found.vehicles ?? []).forEach((item) => {
        const plate = normalizeIdentifier(item.plate);
        const chassis = normalizeIdentifier(item.chassis);
        if (plate) existingKeys.add(`P:${plate}`);
        if (chassis) existingKeys.add(`C:${chassis}`);
      });

      const isAlreadyRegistered = (vehicle: LookupVehicle) => {
        const plate = normalizeIdentifier(vehicle.plate);
        const chassis = normalizeIdentifier(vehicle.chassis);
        return (
          (!!plate && existingKeys.has(`P:${plate}`)) ||
          (!!chassis && existingKeys.has(`C:${chassis}`))
        );
      };

      // Novos vêm marcados; já cadastrados vêm desmarcados
      const preselected: Record<string, boolean> = {};
      const pending: LookupVehicle[] = [];
      (data.vehicles ?? []).forEach((vehicle) => {
        const already = isAlreadyRegistered(vehicle);
        preselected[vehicle.external_key] = !already;
        if (!already) pending.push(vehicle);
      });
      setSelectedVehicles(preselected);

      // Enriquecimento só para veículos que serão realmente criados
      if (pending.length > 0) {
        setIsEnriching(true);
        try {
          await enrichAll(pending);
        } finally {
          setIsEnriching(false);
        }
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Falha na consulta');
    }
  };

  /** Liga/desliga a atualização de um veículo já cadastrado (consulta sob demanda) */
  const handleToggleUpdate = async (vehicle: LookupVehicle, value: boolean) => {
    setUpdateEnabled((prev) => ({ ...prev, [vehicle.external_key]: value }));
    if (!value) {
      setFieldSelection((prev) => ({ ...prev, [vehicle.external_key]: {} }));
      return;
    }
    // Só agora gasta crédito de consulta FIPE/placa
    if (!enrichment[vehicle.external_key]) {
      setIsEnriching(true);
      try {
        await enrichVehicle(vehicle);
      } finally {
        setIsEnriching(false);
      }
    }
  };

  /** Marca/desmarca um campo específico da atualização */
  const toggleField = (key: string, field: UpdatableVehicleField, value: boolean) => {
    setFieldSelection((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? {}), [field]: value },
    }));
  };

  /** Diferenças e campos autorizados de um veículo já cadastrado */
  const getDiffs = useCallback(
    (vehicle: LookupVehicle, existing: ExistingVehicleMatch): VehicleFieldDiff[] => {
      const canonical = buildCanonicalImportedVehicle(vehicle, enrichment[vehicle.external_key]);
      return buildVehicleFieldDiffs(existing.current, canonical);
    },
    [enrichment],
  );

  /** Campos efetivamente autorizados (nunca sobrescreve com valor vazio) */
  const authorizedFields = useCallback(
    (vehicle: LookupVehicle, diffs: VehicleFieldDiff[]): UpdatableVehicleField[] => {
      const selection = fieldSelection[vehicle.external_key] ?? {};
      return diffs
        .filter((diff) => !diff.incomingIsEmpty && selection[diff.field])
        .map((diff) => diff.field);
    },
    [fieldSelection],
  );

  const foundCustomer = result?.found ? result.customer : null;
  const existingCustomer = matches?.customer ?? null;

  /** Resumo do que será gravado */
  const saveSummary = useMemo(() => {
    const vehicles = result?.vehicles ?? [];
    let toCreate = 0;
    let toUpdate = 0;
    vehicles.forEach((vehicle) => {
      const existing = findExisting(vehicle);
      if (!existing) {
        if (selectedVehicles[vehicle.external_key]) toCreate += 1;
        return;
      }
      if (updateEnabled[vehicle.external_key]) {
        const fields = authorizedFields(vehicle, getDiffs(vehicle, existing));
        if (fields.length > 0) toUpdate += 1;
      }
    });
    return { toCreate, toUpdate };
  }, [
    result,
    selectedVehicles,
    updateEnabled,
    findExisting,
    getDiffs,
    authorizedFields,
  ]);

  /**
   * v1.5.0 — Veículos NOVOS selecionados que ainda não possuem vínculo relacional
   * válido (marca/modelo FIPE ou categoria do tenant). Bloqueiam a gravação.
   */
  const unresolvedVehicles = useMemo(() => {
    return (result?.vehicles ?? []).filter((vehicle) => {
      if (findExisting(vehicle)) return false;
      if (!selectedVehicles[vehicle.external_key]) return false;
      const canonical = buildCanonicalImportedVehicle(vehicle, enrichment[vehicle.external_key]);
      return !canonical.fipe_resolved;
    });
  }, [result, selectedVehicles, findExisting, enrichment]);

  const nothingToDo =
    !!existingCustomer && saveSummary.toCreate === 0 && saveSummary.toUpdate === 0;

  /** PASSO FINAL — grava contato, cliente e veículos em uma única transação */
  const handleSave = async () => {
    if (!result?.customer) return;
    const customer = result.customer;

    setIsSaving(true);
    try {
      const payload: Array<Record<string, unknown>> = [];

      for (const vehicle of result.vehicles ?? []) {
        const existing = findExisting(vehicle);
        const resolvedMatch = enrichment[vehicle.external_key];
        const canonical = buildCanonicalImportedVehicle(vehicle, resolvedMatch);

        // ------------------------- Veículo novo -------------------------
        if (!existing) {
          if (!selectedVehicles[vehicle.external_key]) continue;

          if (!canonical.fipe_resolved) {
            throw new Error(
              `Revise o veículo ${vehicle.plate ?? vehicleLabel(vehicle)}: defina marca e modelo (ou categoria) antes de importar`,
            );
          }
          payload.push({ ...canonical });
          continue;
        }

        // ------------------ Veículo já cadastrado -----------------------
        if (!updateEnabled[vehicle.external_key]) continue;

        const fields = authorizedFields(vehicle, getDiffs(vehicle, existing));
        if (fields.length === 0) continue;

        if (
          fields.some((field) => FIPE_DEPENDENT_FIELDS.includes(field)) &&
          !canonical.fipe_resolved
        ) {
          throw new Error(
            `Atualizar marca/modelo/ano do veículo ${existing.plate ?? ''} exige correspondência FIPE confirmada`,
          );
        }

        payload.push({
          ...canonical,
          vehicle_id: existing.vehicle_id,
          update_fields: fields,
        });
      }

      if (existingCustomer && payload.length === 0) {
        throw new Error('Selecione ao menos um veículo para importar ou atualizar');
      }

      const { data, error } = await (supabase.rpc as any)('import_customer_with_vehicles', {
        p_customer: {
          name: customer.name ?? 'Cliente sem nome',
          document_digits: customer.document_digits,
          phone: customer.phone || null,
          email: customer.email || null,
          address: customer.address,
        },
        p_vehicles: payload,
        p_existing_customer_id: existingCustomer?.customer_id ?? null,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw new Error(error.message);

      const customerId = data?.customer_id as string | undefined;
      if (!customerId) throw new Error('O cadastro não retornou o identificador do cliente');

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['crm-customers'] }),
        queryClient.invalidateQueries({ queryKey: ['crm-customers-paginated'] }),
        queryClient.invalidateQueries({ queryKey: ['customer-vehicles'] }),
        queryClient.invalidateQueries({ queryKey: ['customer-modal-data'] }),
        queryClient.invalidateQueries({ queryKey: ['tenant-vehicles'] }),
        queryClient.invalidateQueries({ queryKey: ['tenant-vehicles-kpis'] }),
        queryClient.invalidateQueries({ queryKey: ['vehicle-timeline'] }),
      ]);

      const created = (data?.created_count as number) ?? 0;
      const updated = (data?.updated_count as number) ?? 0;
      toast.success(
        existingCustomer
          ? `Cliente já cadastrado: ${created} veículo(s) importado(s), ${updated} atualizado(s)`
          : `Cliente criado com ${created} veículo(s) importado(s)`,
      );
      handleOpenChange(false);
      onSuccess?.(customerId);
    } catch (error) {
      logger.error('CustomerAutoImport', 'Falha no cadastro automático', {
        message: error instanceof Error ? error.message : String(error),
      });
      toast.error(error instanceof Error ? error.message : 'Falha ao cadastrar cliente');
    } finally {
      setIsSaving(false);
      setIsEnriching(false);
    }
  };

  const isChecking = lookup.isPending || existingMatches.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* Altura fixa por viewport (mesmo padrão do modal de anotações do chat):
          cabeçalho e rodapé fixos, corpo com rolagem interna. */}
      <DialogContent className="max-w-3xl w-[95vw] h-[90dvh] md:h-[85dvh] flex flex-col gap-0 p-0 overflow-hidden">
        <DialogHeader className="p-4 border-b shrink-0">
          <DialogTitle className="text-base">Cadastro automático de cliente</DialogTitle>
          <DialogDescription className="text-xs">
            Consulte o cliente no sistema externo pelo CPF/CNPJ e importe também os veículos.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0">

          <div className="p-4 space-y-4">
            {/* ============================ CONSULTA ============================ */}
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              {connections.length > 1 && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Origem</Label>
                  <Select
                    value={effectiveConnectionId ?? undefined}
                    onValueChange={setConnectionId}
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder="Selecione a conexão" />
                    </SelectTrigger>
                    <SelectContent>
                      {connections.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} · {c.provider_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="text-xs">CPF ou CNPJ</Label>
                <div className="flex gap-2">
                  <Input
                    value={document}
                    onChange={(e) => setDocument(applyDocumentMask(e.target.value))}
                    placeholder="000.000.000-00"
                    inputMode="numeric"
                    className="h-9 text-sm"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && isDocumentValid) handleSearch();
                    }}
                  />
                  <Button
                    size="sm"
                    onClick={handleSearch}
                    disabled={!isDocumentValid || isChecking || isLoadingConnections}
                  >
                    {isChecking ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                    <span className="ml-1.5 hidden sm:inline">Consultar</span>
                  </Button>
                </div>
              </div>
            </div>

            {/* ===================== RESULTADO / MENSAGENS ===================== */}
            {result && !result.found && (
              <InlineNotice variant="warning">
                {result.message || 'Nenhum resultado encontrado para o documento informado.'}
              </InlineNotice>
            )}

            {foundCustomer && (
              <>
                {/* -------- Dados do cliente -------- */}
                <div className="rounded-lg border p-3 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{foundCustomer.name}</span>
                    <Badge variant="secondary" className="text-[10px] uppercase">
                      {customerType === 'pj' ? 'Pessoa Jurídica' : 'Pessoa Física'}
                    </Badge>
                    {existingCustomer && (
                      <Badge variant="outline" className="text-[10px] uppercase">
                        Já cadastrado
                      </Badge>
                    )}
                  </div>

                  {/* Sinalização de duplicidade do cliente */}
                  {existingCustomer && (
                    <InlineNotice variant="warning">
                      Este cliente já está cadastrado
                      {existingCustomer.name ? ` como "${existingCustomer.name}"` : ''}. Os dados
                      cadastrais dele não serão alterados — você pode importar apenas os veículos
                      selecionados abaixo.
                    </InlineNotice>
                  )}

                  <div className="grid gap-1.5 text-xs text-muted-foreground sm:grid-cols-2">
                    <span>Documento: {foundCustomer.document}</span>
                    {foundCustomer.phone && (
                      <span className="flex items-center gap-1.5">
                        <Phone className="h-3 w-3" /> {foundCustomer.phone}
                      </span>
                    )}
                    {foundCustomer.email && (
                      <span className="flex items-center gap-1.5">
                        <Mail className="h-3 w-3" /> {foundCustomer.email}
                      </span>
                    )}
                    {foundCustomer.address?.cidade && (
                      <span className="flex items-center gap-1.5">
                        <MapPin className="h-3 w-3" />
                        {[foundCustomer.address.cidade, foundCustomer.address.estado]
                          .filter(Boolean)
                          .join(' / ')}
                      </span>
                    )}
                    {foundCustomer.address?.logradouro && (
                      <span className="sm:col-span-2">
                        {[
                          foundCustomer.address.logradouro,
                          foundCustomer.address.numero,
                          foundCustomer.address.bairro,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      </span>
                    )}
                  </div>
                </div>

                {/* -------- Veículos retornados -------- */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">
                      Veículos encontrados ({result?.vehicles.length ?? 0})
                    </Label>
                    {(result?.vehicles.length ?? 0) > 0 && (
                      <span className="text-[11px] text-muted-foreground">
                        Selecione quais deseja cadastrar
                      </span>
                    )}
                  </div>

                  {(result?.vehicles.length ?? 0) === 0 ? (
                    <InlineNotice variant="info">
                      Nenhum veículo retornado para este cliente.
                    </InlineNotice>
                  ) : (
                    <div className="space-y-2">
                      {result!.vehicles.map((vehicle) => {
                        const key = vehicle.external_key;
                        const existing = findExisting(vehicle);
                        const checked = !!selectedVehicles[key];
                        const wantsUpdate = !!updateEnabled[key];
                        const match = enrichment[key];
                        const meta =
                          match && match.status !== 'idle' ? ENRICHMENT_META[match.status] : null;
                        const displayLabel =
                          [match?.brand_name, match?.model_name].filter(Boolean).join(' ') ||
                          vehicleLabel(vehicle);
                        const diffs = existing && wantsUpdate ? getDiffs(vehicle, existing) : [];
                        const selection = fieldSelection[key] ?? {};
                        // v1.5.0: precisa de resolução manual quando não há vínculo válido
                        const needsManual =
                          !existing &&
                          !buildCanonicalImportedVehicle(vehicle, match).fipe_resolved;

                        return (
                          <div
                            key={key}
                            className={cn(
                              'rounded-lg border p-3 transition-colors',
                              existing
                                ? 'bg-muted/30'
                                : checked
                                  ? 'border-primary/50 bg-primary/5'
                                  : 'hover:bg-muted/50',
                            )}
                          >
                            <div className="flex items-start gap-3">
                              {!existing && (
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={(value) =>
                                    setSelectedVehicles((prev) => ({ ...prev, [key]: !!value }))
                                  }
                                  className="mt-0.5"
                                />
                              )}
                              <div className="min-w-0 flex-1 space-y-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Car className="h-3.5 w-3.5 text-muted-foreground" />
                                  <span className="text-sm font-medium truncate">
                                    {displayLabel}
                                  </span>
                                  {vehicle.plate && (
                                    <Badge variant="outline" className="text-[10px]">
                                      {vehicle.plate}
                                    </Badge>
                                  )}
                                  {existing && (
                                    <Badge variant="secondary" className="text-[10px] uppercase">
                                      Já cadastrado
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-[11px] text-muted-foreground">
                                  {[
                                    vehicle.raw_type,
                                    match?.year ?? vehicle.raw_year,
                                    vehicle.color,
                                    match?.fuel ?? vehicle.fuel,
                                    vehicle.coverage_number
                                      ? `Cobertura ${vehicle.coverage_number}`
                                      : null,
                                  ]
                                    .filter(Boolean)
                                    .join(' • ')}
                                </div>

                                {existing?.belongs_to_customer_name && (
                                  <div className="text-[11px] text-muted-foreground">
                                    Vinculado a: {existing.belongs_to_customer_name}
                                  </div>
                                )}

                                {/* Situação FIPE (somente quando houve consulta) */}
                                {meta && (
                                  <div className="flex items-center gap-2 pt-0.5">
                                    {match?.status === 'loading' && (
                                      <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                                    )}
                                    <span className={cn('text-[11px]', meta.className)}>
                                      {meta.label}
                                    </span>
                                    {match?.status !== 'loading' && (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 px-1.5 text-[11px]"
                                        onClick={() => void enrichVehicle(vehicle, true)}
                                      >
                                        <RefreshCw className="h-3 w-3 mr-1" />
                                        Resolver FIPE
                                      </Button>
                                    )}
                                  </div>
                                )}

                                {/* ---- v1.5.0: resolução manual (sem FIPE) ---- */}
                                {!existing && checked && needsManual && (
                                  <VehicleManualResolver
                                    vehicle={vehicle}
                                    enrichment={match}
                                    onApply={(value) => setManualMatch(key, value)}
                                  />
                                )}

                                {/* ---- Veículo existente: atualizar dados? ---- */}
                                {existing && (
                                  <label className="mt-2 flex items-center gap-2 cursor-pointer">
                                    <Checkbox
                                      checked={wantsUpdate}
                                      onCheckedChange={(value) =>
                                        void handleToggleUpdate(vehicle, !!value)
                                      }
                                    />
                                    <span className="text-[11px]">
                                      Atualizar dados deste veículo com o retorno da integração
                                    </span>
                                  </label>
                                )}

                                {/* ---- Comparação campo a campo ---- */}
                                {existing && wantsUpdate && (
                                  <div className="mt-2 rounded-md border bg-background p-2 space-y-2">
                                    {diffs.length === 0 ? (
                                      <p className="text-[11px] text-muted-foreground">
                                        Nenhuma diferença entre o cadastro atual e o retorno da
                                        integração.
                                      </p>
                                    ) : (
                                      <>
                                        <div className="flex items-center gap-2">
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="h-6 px-2 text-[11px]"
                                            onClick={() =>
                                              setFieldSelection((prev) => ({
                                                ...prev,
                                                [key]: Object.fromEntries(
                                                  diffs
                                                    .filter((d) => !d.incomingIsEmpty)
                                                    .map((d) => [d.field, true]),
                                                ),
                                              }))
                                            }
                                          >
                                            Marcar todos os divergentes
                                          </Button>
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="h-6 px-2 text-[11px]"
                                            onClick={() =>
                                              setFieldSelection((prev) => ({
                                                ...prev,
                                                [key]: Object.fromEntries(
                                                  diffs
                                                    .filter(
                                                      (d) => !d.incomingIsEmpty && d.currentIsEmpty,
                                                    )
                                                    .map((d) => [d.field, true]),
                                                ),
                                              }))
                                            }
                                          >
                                            Somente campos vazios
                                          </Button>
                                        </div>

                                        <div className="grid grid-cols-[1.2rem_1fr_1fr] gap-x-2 gap-y-1 text-[11px]">
                                          <span />
                                          <span className="font-medium text-muted-foreground">
                                            No sistema
                                          </span>
                                          <span className="font-medium text-muted-foreground">
                                            Retornado pela integração
                                          </span>

                                          {diffs.map((diff) => (
                                            <div key={diff.field} className="contents">
                                              <Checkbox
                                                checked={!!selection[diff.field]}
                                                disabled={diff.incomingIsEmpty}
                                                onCheckedChange={(value) =>
                                                  toggleField(key, diff.field, !!value)
                                                }
                                                className="mt-0.5"
                                              />
                                              <span className="min-w-0">
                                                <span className="block text-muted-foreground">
                                                  {diff.label}
                                                </span>
                                                <span className="block break-words">
                                                  {diff.currentValue ?? '—'}
                                                </span>
                                              </span>
                                              <span className="min-w-0 break-words">
                                                {diff.incomingIsEmpty ? (
                                                  <span className="text-muted-foreground">
                                                    Sem dado na integração
                                                  </span>
                                                ) : (
                                                  diff.incomingValue
                                                )}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {unresolvedVehicles.length > 0 && (
                  <InlineNotice variant="warning">
                    {unresolvedVehicles.length} veículo(s) selecionado(s) ainda sem marca/modelo
                    (ou categoria). Defina os dados manualmente para manter os relacionamentos do
                    sistema íntegros.
                  </InlineNotice>
                )}

                {nothingToDo && (
                  <InlineNotice variant="info">
                    Cliente e veículos retornados já estão cadastrados. Selecione algum veículo
                    para atualizar caso queira trazer dados da integração.
                  </InlineNotice>
                )}
              </>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="border-t p-3 gap-2 sm:justify-between shrink-0">
          <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={
              !foundCustomer ||
              isSaving ||
              isEnriching ||
              nothingToDo ||
              unresolvedVehicles.length > 0
            }
          >
            {isSaving || isEnriching ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-1.5" />
            )}
            {isEnriching
              ? 'Validando veículos'
              : existingCustomer
                ? 'Importar veículos selecionados'
                : 'Cadastrar cliente'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// SUBCOMPONENTE — Resolução manual de veículo (v1.5.0)
// =============================================================================

interface VehicleManualResolverProps {
  vehicle: LookupVehicle;
  enrichment?: VehicleEnrichment;
  onApply: (value: Omit<VehicleEnrichment, 'status' | 'source'>) => void;
}

/**
 * Painel inline exibido quando o provider não trouxe dados compatíveis com a
 * base FIPE. Reaproveita os mesmos seletores do cadastro manual de veículo
 * (marca → modelo → ano) e, para o tipo "Outros", a categoria do tenant.
 */
function VehicleManualResolver({ vehicle, enrichment, onApply }: VehicleManualResolverProps) {
  const { activeCategories } = useVehiclesModule();

  const [vehicleType, setVehicleType] = useState<VehicleType>(
    (enrichment?.vehicle_type as VehicleType) ?? vehicle.vehicle_type ?? 'CAR',
  );
  const [brandId, setBrandId] = useState<string | null>(enrichment?.brand_id ?? null);
  const [modelId, setModelId] = useState<string | null>(enrichment?.model_id ?? null);
  const [yearCode, setYearCode] = useState<string | null>(enrichment?.year_code ?? null);
  const [categoryId, setCategoryId] = useState<string | null>(enrichment?.category_id ?? null);

  const fipeType = (vehicleType !== 'OTHER' ? vehicleType : null) as FipeVehicleType | null;
  const brandsQuery = useFipeBrands(fipeType);
  const modelsQuery = useFipeModels(fipeType, brandId);
  const yearsQuery = useFipeYears(fipeType, modelId);

  /** Aplica a seleção atual ao estado de enriquecimento do wizard */
  const apply = (next: Partial<{
    vehicleType: VehicleType;
    brandId: string | null;
    modelId: string | null;
    yearCode: string | null;
    categoryId: string | null;
  }>) => {
    const type = next.vehicleType ?? vehicleType;
    const bId = next.brandId !== undefined ? next.brandId : brandId;
    const mId = next.modelId !== undefined ? next.modelId : modelId;
    const yCode = next.yearCode !== undefined ? next.yearCode : yearCode;
    const cId = next.categoryId !== undefined ? next.categoryId : categoryId;

    const brandName = (brandsQuery.data ?? []).find((b) => b.id === bId)?.nome_marca ?? null;
    const modelName = (modelsQuery.data ?? []).find((m) => m.id === mId)?.nome_modelo ?? null;
    const yearItem = (yearsQuery.data ?? []).find((y) => y.codigo_ano === yCode);

    onApply({
      vehicle_type: type,
      category_id: type === 'OTHER' ? cId : null,
      brand_id: type === 'OTHER' ? null : bId,
      brand_name: type === 'OTHER' ? null : brandName,
      model_id: type === 'OTHER' ? null : mId,
      model_name: type === 'OTHER' ? null : modelName,
      year_code: type === 'OTHER' ? null : yCode,
      year: yearItem?.ano ?? enrichment?.year ?? (Number(vehicle.raw_year) || null),
      fuel: yearItem?.combustivel ?? enrichment?.fuel ?? vehicle.fuel ?? null,
      fipe_code: enrichment?.fipe_code ?? null,
      fipe_value: enrichment?.fipe_value ?? null,
    });
  };

  return (
    <div className="mt-2 rounded-md border bg-background p-2 space-y-2">
      <div className="flex items-center gap-1.5 text-[11px] font-medium">
        <Wrench className="h-3 w-3 text-muted-foreground" />
        Definir dados manualmente
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {/* Tipo */}
        <div className="space-y-1">
          <Label className="text-[11px]">Tipo</Label>
          <Select
            value={vehicleType}
            onValueChange={(value) => {
              const type = value as VehicleType;
              setVehicleType(type);
              setBrandId(null);
              setModelId(null);
              setYearCode(null);
              apply({ vehicleType: type, brandId: null, modelId: null, yearCode: null });
            }}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(VEHICLE_TYPE_LABELS) as VehicleType[]).map((type) => (
                <SelectItem key={type} value={type} className="text-xs">
                  {VEHICLE_TYPE_LABELS[type]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {vehicleType === 'OTHER' ? (
          /* Categoria própria do tenant (fora da FIPE) */
          <div className="space-y-1">
            <Label className="text-[11px]">Categoria</Label>
            <Select
              value={categoryId ?? undefined}
              onValueChange={(value) => {
                setCategoryId(value);
                apply({ categoryId: value });
              }}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Selecione a categoria" />
              </SelectTrigger>
              <SelectContent>
                {activeCategories.map((category) => (
                  <SelectItem key={category.id} value={category.id} className="text-xs">
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {activeCategories.length === 0 && (
              <p className="text-[10px] text-muted-foreground">
                Nenhuma categoria ativa cadastrada no módulo de veículos.
              </p>
            )}
          </div>
        ) : (
          <>
            {/* Marca */}
            <div className="space-y-1">
              <Label className="text-[11px]">Marca</Label>
              <Select
                value={brandId ?? undefined}
                disabled={brandsQuery.isLoading}
                onValueChange={(value) => {
                  setBrandId(value);
                  setModelId(null);
                  setYearCode(null);
                  apply({ brandId: value, modelId: null, yearCode: null });
                }}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue
                    placeholder={brandsQuery.isLoading ? 'Carregando...' : 'Selecione a marca'}
                  />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {(brandsQuery.data ?? []).map((brand) => (
                    <SelectItem key={brand.id} value={brand.id} className="text-xs">
                      {brand.nome_marca}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Modelo */}
            <div className="space-y-1">
              <Label className="text-[11px]">Modelo</Label>
              <Select
                value={modelId ?? undefined}
                disabled={!brandId || modelsQuery.isLoading}
                onValueChange={(value) => {
                  setModelId(value);
                  setYearCode(null);
                  apply({ modelId: value, yearCode: null });
                }}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue
                    placeholder={modelsQuery.isLoading ? 'Carregando...' : 'Selecione o modelo'}
                  />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {(modelsQuery.data ?? []).map((model) => (
                    <SelectItem key={model.id} value={model.id} className="text-xs">
                      {model.nome_modelo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Ano/combustível */}
            <div className="space-y-1">
              <Label className="text-[11px]">Ano</Label>
              <Select
                value={yearCode ?? undefined}
                disabled={!modelId || yearsQuery.isLoading}
                onValueChange={(value) => {
                  setYearCode(value);
                  apply({ yearCode: value });
                }}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue
                    placeholder={yearsQuery.isLoading ? 'Carregando...' : 'Selecione o ano'}
                  />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {(yearsQuery.data ?? []).map((year) => (
                    <SelectItem
                      key={year.codigo_ano}
                      value={year.codigo_ano}
                      className="text-xs"
                    >
                      {year.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
